import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Save, Plus, X, BookOpen, Building2, Sprout, Briefcase, FlaskConical, GraduationCap, Theater, Home, Bold, Italic, List, ListOrdered, Trash2, Upload } from 'lucide-react';


import { toast } from 'sonner';
import { getFileUrl } from '../../utils/assetUrlHelper';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SERVICES = [
  { slug: 'library', label: 'Library', icon: BookOpen },
  { slug: 'museum', label: 'Museum', icon: Building2 },
  { slug: 'nursery', label: 'Nursery', icon: Sprout },
  { slug: 'consultancy', label: 'Consultancy', icon: Briefcase },
  { slug: 'contract-research', label: 'Contract Research', icon: FlaskConical },
  { slug: 'education', label: 'Education', icon: GraduationCap },
  { slug: 'auditorium', label: 'Auditorium', icon: Theater },
  { slug: 'guest-house', label: 'Guest House', icon: Home }
];

// TipTap Rich Text Editor
function RichTextEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false })],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: { attributes: { class: 'prose prose-sm max-w-none min-h-[150px] p-4 focus:outline-none' } },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || '');
  }, [value, editor]);

  if (!editor) return <div className="border rounded p-4 min-h-[150px] bg-gray-50 animate-pulse" />;

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="flex items-center gap-1 px-2 py-2 bg-gray-50 border-b">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}><Bold className="w-4 h-4" /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}><Italic className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}><List className="w-4 h-4" /></button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200 text-[#0E6D2B]' : 'text-gray-600'}`}><ListOrdered className="w-4 h-4" /></button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export default function ServicesManagementPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('library');
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAllServices(); }, []);

  const fetchAllServices = async () => {
    try {
      setLoading(true);
      const promises = SERVICES.map(s => axios.get(`${BACKEND_URL}/api/services/${s.slug}?published_only=false`).catch(() => null));
      const results = await Promise.all(promises);
      const data = {};
      results.forEach((r, i) => {
        data[SERVICES[i].slug] = r?.data || { slug: SERVICES[i].slug, page_title: SERVICES[i].label, contact: {}, gallery: [], resources: [], reference_books: [], plants: [], consultancy_offered: [], ongoing: [], completed: [], facilities: [], contacts: [], sections: [] };
      });
      setServices(data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (slug, field, value) => {
    setServices(prev => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }));
  };

  const handleSave = async (slug, dataOverride = null) => {
    try {
      const data = dataOverride || services[slug];
      if (data.id) {
        await axios.put(`${BACKEND_URL}/api/services/${slug}`, data, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${BACKEND_URL}/api/services`, data, { headers: { Authorization: `Bearer ${token}` } });
      }
      toast.success('Saved successfully');
      fetchAllServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleGalleryImageUpload = async (slug, index, file) => {
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${BACKEND_URL}/api/files/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Update the gallery image in the service
      setServices(prev => {
        const gallery = [...(prev[slug]?.gallery || [])];
        if (gallery[index]) {
          gallery[index] = { ...gallery[index], image: response.data.file_path };
        }
        return {
          ...prev,
          [slug]: { ...prev[slug], gallery }
        };
      });

      toast.success('Gallery image uploaded');
    } catch (error) {
      toast.error('Failed to upload gallery image');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E6D2B]"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Services Management</h1>
        <p className="text-gray-600 text-sm">Manage all 8 service pages - fields map 1:1 to frontend</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 lg:grid-cols-8 gap-2 bg-transparent h-auto p-0 mb-6">
          {SERVICES.map(s => {
            const Icon = s.icon;
            return (
              <TabsTrigger key={s.slug} value={s.slug} className="flex flex-col gap-2 p-4 border rounded-lg data-[state=active]:bg-[#0E6D2B] data-[state=active]:text-white">
                <Icon className="w-5 h-5" />
                <span className="text-xs">{s.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {SERVICES.map(s => (
          <TabsContent key={s.slug} value={s.slug}>
            <ServiceForm
              service={services[s.slug]}
              slug={s.slug}
              updateField={updateField}
              onSave={(data) => handleSave(s.slug, data)}
              onGalleryImageUpload={(index, file) => handleGalleryImageUpload(s.slug, index, file)}
            />
          </TabsContent>
        ))}
      </Tabs>
    </AdminLayout>
  );
}


const normalizePath = (path) => {
  if (!path) return '';
  console.log('[normalizePath] INPUT:', path);
  let p = path;
  
  // If it's a full URL, extract the path
  if (typeof p === 'string' && p.startsWith('http')) {
    try {
      const url = new URL(p);
      p = url.pathname;
    } catch (e) {
      // Fallback: check if domain is in string and remove it manually
      const match = p.match(/\/\/[^\/]+(\/.*)/);
      if (match) p = match[1];
    }
  }
  
  if (typeof p !== 'string') return '';

  // Remove leading slash
  if (p.startsWith('/')) p = p.slice(1);
  
  // Remove common prefixes
  p = p.replace(/^api\/uploads\//, '');
  p = p.replace(/^data\/files\//, '');
  
  console.log('[normalizePath] OUTPUT:', p);
  return p;
};

function ServiceForm({ service, slug, updateField, onSave, onGalleryImageUpload }) {
  const [fileData, setFileData] = useState({});
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  if (!service) return null;

  const getAuthHeaders = () => ({ Authorization: `Bearer ${token}` });

  const uploadFiles = async () => {
    const uploadedUrls = {};
    const uploadPromises = [];

    for (const [key, files] of Object.entries(fileData)) {
      const fileList = Array.isArray(files) ? files : [files];

      for (const file of fileList) {
        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);
          // Use 'services' subfolder or specific one if needed
          formData.append('subfolder', 'services');

          // Move subfolder to query parameter
          const promise = axios.post(`${BACKEND_URL}/api/files/upload?subfolder=services`, formData, {
            headers: {
              ...getAuthHeaders(),
              'Content-Type': 'multipart/form-data'
            }
          }).then(response => {
            let url = response.data.file_path;
            if (url) {
              if (url.includes('data/files/')) url = url.replace('data/files/', '');
              if (!url.startsWith('/uploads/')) url = '/uploads/' + (url.startsWith('/') ? url.slice(1) : url);
            }
            if (!uploadedUrls[key]) uploadedUrls[key] = [];
            uploadedUrls[key].push(url);
          });

          uploadPromises.push(promise);
        }
      }
    }

    if (uploadPromises.length > 0) {
      toast.info(`Uploading ${uploadPromises.length} files...`);
      await Promise.all(uploadPromises);
    }

    return uploadedUrls;
  };

  const handleSaveWrapper = async () => {
    setSaving(true);
    try {
      const uploadedUrls = await uploadFiles();
      const updatedService = { ...service };

      // Apply banner image
      if (uploadedUrls.banner_image && uploadedUrls.banner_image.length > 0) {
        updatedService.banner_image = uploadedUrls.banner_image[0];
      }

      // Apply gallery images
      if (uploadedUrls.gallery && uploadedUrls.gallery.length > 0) {
        updatedService.gallery = [
          ...(updatedService.gallery || []),
          ...uploadedUrls.gallery.map(url => ({ image: url, alt: '', order: (updatedService.gallery?.length || 0) }))
        ];
      }

      // Apply museum sections images
      // Keys will be like 'section_images_0', 'section_images_1'
      if (updatedService.sections) {
        updatedService.sections = updatedService.sections.map((section, idx) => {
          const key = `section_images_${idx}`;
          if (uploadedUrls[key]) {
            return {
              ...section,
              images: [
                ...(section.images || []),
                ...uploadedUrls[key].map(url => ({ image: url, alt: '', order: (section.images?.length || 0) }))
              ]
            };
          }
          return section;
        });
      }

      // Auditorium / Nursery / etc specific galleries are handled by the generic 'gallery' key above if they map to 'gallery' field
      // Note: Library, Museum, Auditorium, Nursery ALL map to 'gallery' field in the schema.

      onSave(updatedService);
      setFileData({});
    } finally {
      setSaving(false);
    }
  }

  const addGalleryImage = () => {
    const gallery = service.gallery || [];
    updateField(slug, 'gallery', [...gallery, { image: '', alt: '', order: gallery.length }]);
  };

  const removeGalleryImage = (index) => {
    const gallery = [...(service.gallery || [])];
    gallery.splice(index, 1);
    updateField(slug, 'gallery', gallery);
  };

  const updateGalleryImage = (index, field, value) => {
    const gallery = [...(service.gallery || [])];
    gallery[index] = { ...gallery[index], [field]: value };
    updateField(slug, 'gallery', gallery);
  };

  const addArrayItem = (field, template = '') => {
    const arr = service[field] || [];
    updateField(slug, field, [...arr, template]);
  };

  const removeArrayItem = (field, index) => {
    const arr = [...(service[field] || [])];
    arr.splice(index, 1);
    updateField(slug, field, arr);
  };

  const updateArrayItem = (field, index, value) => {
    const arr = [...(service[field] || [])];
    arr[index] = value;
    updateField(slug, field, arr);
  };

  const updateObjectArrayItem = (field, index, key, value) => {
    const arr = [...(service[field] || [])];
    arr[index] = { ...arr[index], [key]: value };
    updateField(slug, field, arr);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-end bg-white p-4 rounded-lg border">
        <Button onClick={handleSaveWrapper} disabled={saving}>
          {saving ? <Upload className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Basic Info */}
      <div className="bg-white p-6 rounded-lg border space-y-4">
        <h3 className="font-bold text-lg">Basic Information</h3>
        <div>
          <label className="block text-sm font-medium mb-2">Page Title</label>
          <input type="text" value={service.page_title || ''} onChange={(e) => updateField(slug, 'page_title', e.target.value)} className="w-full px-4 py-2 border rounded" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Banner Image</label>
          <FileUploadInput
            field={{ name: 'banner_image', type: 'file', fileType: 'image' }}
            value={fileData.banner_image}
            existingUrl={service.banner_image}
            // We use manual save wrapper, so explicit setFormData not strictly needed for the 'value' but good for feedback
            // Actually FileUploadInput uses formData[field.name] to show existingUrl if not passed explicitly? 
            // We passed existingUrl explicitly.
            onChange={(files) => setFileData({ ...fileData, banner_image: files })}
            onDeleteServerFile={() => { updateField(slug, 'banner_image', ''); return true; }}
          />
        </div>
      </div>

      {/* About/Introduction Content */}
      <div className="bg-white p-6 rounded-lg border">
        <h3 className="font-bold text-lg mb-4">About / Introduction (Rich Text)</h3>
        <RichTextEditor
          value={service.intro_html || service.about_html || ''}
          onChange={(html) => {
            updateField(slug, 'intro_html', html);
            if (slug === 'library') updateField(slug, 'about_html', html);
          }}
        />
      </div>

      {/* LIBRARY SPECIFIC */}
      {slug === 'library' && (
        <>
          {/* Library Timings */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Library Timings</h3>
            <textarea value={service.library_timings || ''} onChange={(e) => updateField(slug, 'library_timings', e.target.value)} rows={3} className="w-full px-4 py-2 border rounded" />
          </div>

          {/* Library Gallery */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Library Gallery (Repeatable Images)</h3>
            <FileUploadInput
              field={{ name: 'gallery', type: 'file', multiple: true, fileType: 'image' }}
              value={fileData.gallery}
              existingUrl={service.gallery?.map(g => g.image) || []}
              onChange={(files) => setFileData({ ...fileData, gallery: files })}
              onDeleteServerFile={(url) => {
                const idx = service.gallery?.findIndex(g => normalizePath(g.image) === normalizePath(url));
                if (idx > -1) {
                  const newGallery = [...(service.gallery || [])];
                  newGallery.splice(idx, 1);
                  updateField(slug, 'gallery', newGallery);
                  return true;
                }
                return false;
              }}
            />
          </div>

          {/* Library Resources Table */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Library Resources (Repeatable Rows)</h3>
              <Button onClick={() => addArrayItem('resources', { title: '', copies: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Row</Button>
            </div>
            <div className="text-xs text-gray-600 mb-2">Columns: Title, Number of Copies</div>
            {service.resources?.map((row, i) => (
              <div key={i} className="flex gap-2 mb-2 p-3 border rounded">
                <span className="text-sm font-medium w-8">{i + 1}.</span>
                <input type="text" placeholder="Title" value={row.title || ''} onChange={(e) => updateObjectArrayItem('resources', i, 'title', e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <input type="text" placeholder="Copies" value={row.copies || ''} onChange={(e) => updateObjectArrayItem('resources', i, 'copies', e.target.value)} className="w-24 px-3 py-2 border rounded text-sm" />
                <Button onClick={() => removeArrayItem('resources', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          {/* Reference Books Table */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Reference Books (Repeatable Rows)</h3>
              <Button onClick={() => addArrayItem('reference_books', { ref_id: '', title: '', copies: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Row</Button>
            </div>
            <div className="text-xs text-gray-600 mb-2">Columns: Ref ID, Title, Number of Copies</div>
            {service.reference_books?.map((row, i) => (
              <div key={i} className="flex gap-2 mb-2 p-3 border rounded">
                <span className="text-sm font-medium w-8">{i + 1}.</span>
                <input type="text" placeholder="Ref ID" value={row.ref_id || ''} onChange={(e) => updateObjectArrayItem('reference_books', i, 'ref_id', e.target.value)} className="w-32 px-3 py-2 border rounded text-sm" />
                <input type="text" placeholder="Title" value={row.title || ''} onChange={(e) => updateObjectArrayItem('reference_books', i, 'title', e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <input type="text" placeholder="Copies" value={row.copies || ''} onChange={(e) => updateObjectArrayItem('reference_books', i, 'copies', e.target.value)} className="w-24 px-3 py-2 border rounded text-sm" />
                <Button onClick={() => removeArrayItem('reference_books', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          {/* Journals File */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Journals File</h3>
            <input type="text" placeholder="Journals PDF URL" value={service.journals_file || ''} onChange={(e) => updateField(slug, 'journals_file', e.target.value)} className="w-full px-4 py-2 border rounded" />
          </div>
        </>
      )}

      {/* MUSEUM SPECIFIC */}
      {slug === 'museum' && (
        <>
          {/* Museum Main Gallery */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Main Gallery (Top/Carousel - Repeatable Images)</h3>
            <FileUploadInput
              field={{ name: 'gallery', type: 'file', multiple: true, fileType: 'image' }}
              value={fileData.gallery}
              existingUrl={service.gallery?.map(g => g.image) || []}
              onChange={(files) => setFileData({ ...fileData, gallery: files })}
              onDeleteServerFile={(url) => {
                const idx = service.gallery?.findIndex(g => normalizePath(g.image) === normalizePath(url));
                if (idx > -1) {
                  const newGallery = [...(service.gallery || [])];
                  newGallery.splice(idx, 1);
                  updateField(slug, 'gallery', newGallery);
                  return true;
                }
                return false;
              }}
            />
          </div>

          {/* Visiting Hours */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Visiting Hours</h3>
            <textarea value={service.visiting_hours || ''} onChange={(e) => updateField(slug, 'visiting_hours', e.target.value)} rows={3} className="w-full px-4 py-2 border rounded" />
          </div>

          {/* Museum Sections (7) */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Museum Sections (Repeatable - Target: 7 sections)</h3>
            <div className="text-sm text-blue-600 mb-4">Current sections: {service.sections?.length || 0}. Frontend displays all sections entered here.</div>
            {service.sections?.map((section, i) => (
              <div key={i} className="border rounded p-4 mb-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-semibold">Section {i + 1}</span>
                  <Button onClick={() => removeArrayItem('sections', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <input type="text" placeholder="Section Title" value={section.title || ''} onChange={(e) => updateObjectArrayItem('sections', i, 'title', e.target.value)} className="w-full px-3 py-2 border rounded mb-2" />
                <textarea placeholder="Section Description (HTML allowed)" value={section.description_html || ''} onChange={(e) => updateObjectArrayItem('sections', i, 'description_html', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded mb-2" />
                <div className="text-xs font-medium mb-2">Section Images (Repeatable):</div>

                <FileUploadInput
                  field={{ name: `section_images_${i}`, type: 'file', multiple: true, fileType: 'image' }}
                  value={fileData[`section_images_${i}`]}
                  existingUrl={section.images?.map(img => img.image) || []}
                  onChange={(files) => setFileData({ ...fileData, [`section_images_${i}`]: files })}
                  onDeleteServerFile={(url) => {
                    const sections = [...service.sections];
                    const imgIdx = sections[i].images?.findIndex(img => normalizePath(img.image) === normalizePath(url));
                    if (imgIdx > -1) {
                      sections[i].images.splice(imgIdx, 1);
                      updateField(slug, 'sections', sections);
                      return true;
                    }
                    return false;
                  }}
                />

              </div>
            ))}
            <Button onClick={() => addArrayItem('sections', { title: '', description_html: '', images: [], order: service.sections?.length || 0 })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Museum Section</Button>
          </div>
        </>
      )}

      {/* NURSERY SPECIFIC */}
      {slug === 'nursery' && (
        <>
          {/* Nursery Gallery */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Nursery Gallery (Repeatable Images)</h3>
            <FileUploadInput
              field={{ name: 'gallery', type: 'file', multiple: true, fileType: 'image' }}
              value={fileData.gallery}
              existingUrl={service.gallery?.map(g => g.image) || []}
              onChange={(files) => setFileData({ ...fileData, gallery: files })}
              onDeleteServerFile={(url) => {
                const idx = service.gallery?.findIndex(g => normalizePath(g.image) === normalizePath(url));
                if (idx > -1) {
                  const newGallery = [...(service.gallery || [])];
                  newGallery.splice(idx, 1);
                  updateField(slug, 'gallery', newGallery);
                  return true;
                }
                return false;
              }}
            />
          </div>

          {/* Available Plants */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Available Plants (Repeatable Rows)</h3>
              <Button onClick={() => addArrayItem('plants', { plant: '', price: '', status: 'Available' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Plant</Button>
            </div>
            <div className="text-xs text-gray-600 mb-2">Columns: Plant Name, Price, Availability (Available/Limited/Out of stock)</div>
            {service.plants?.map((row, i) => (
              <div key={i} className="flex gap-2 mb-2 p-3 border rounded">
                <span className="text-sm font-medium w-8">{i + 1}.</span>
                <input type="text" placeholder="Plant Name" value={row.plant || ''} onChange={(e) => updateObjectArrayItem('plants', i, 'plant', e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <input type="text" placeholder="Price" value={row.price || ''} onChange={(e) => updateObjectArrayItem('plants', i, 'price', e.target.value)} className="w-28 px-3 py-2 border rounded text-sm" />
                <select value={row.status || 'Available'} onChange={(e) => updateObjectArrayItem('plants', i, 'status', e.target.value)} className="w-32 px-3 py-2 border rounded text-sm">
                  <option>Available</option>
                  <option>Limited</option>
                  <option>Out of stock</option>
                </select>
                <Button onClick={() => removeArrayItem('plants', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          {/* Order Steps */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">How to Order Steps (Repeatable)</h3>
              <Button onClick={() => addArrayItem('order_steps', '')} size="sm"><Plus className="w-4 h-4 mr-1" />Add Step</Button>
            </div>
            {service.order_steps?.map((step, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <span className="text-sm font-medium w-8 pt-2">{i + 1}.</span>
                <input type="text" placeholder={`Step ${i + 1}`} value={step} onChange={(e) => updateArrayItem('order_steps', i, e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <Button onClick={() => removeArrayItem('order_steps', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          {/* Booking Files */}
          <div className="bg-white p-6 rounded-lg border space-y-3">
            <h3 className="font-bold text-lg">Booking Files</h3>
            <div>
              <label className="text-sm font-medium">Allocation PDF URL</label>
              <input type="text" value={service.allocation_pdf || ''} onChange={(e) => updateField(slug, 'allocation_pdf', e.target.value)} className="w-full px-3 py-2 border rounded mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Allotment PDF URL</label>
              <input type="text" value={service.allotment_pdf || ''} onChange={(e) => updateField(slug, 'allotment_pdf', e.target.value)} className="w-full px-3 py-2 border rounded mt-1" />
            </div>
          </div>
        </>
      )}

      {/* CONSULTANCY SPECIFIC */}
      {slug === 'consultancy' && (
        <>
          {/* Consultancy Offered Table */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Consultancy Offered (Repeatable Rows)</h3>
              <Button onClick={() => addArrayItem('consultancy_offered', { year_range: '', number: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Row</Button>
            </div>
            <div className="text-xs text-gray-600 mb-2">Columns: Serial (auto), Year Range, Number of Consultancies</div>
            {service.consultancy_offered?.map((row, i) => (
              <div key={i} className="flex gap-2 mb-2 p-3 border rounded">
                <span className="text-sm font-medium w-16">#{i + 1}</span>
                <input type="text" placeholder="Year Range (e.g., 2020-2021)" value={row.year_range || ''} onChange={(e) => updateObjectArrayItem('consultancy_offered', i, 'year_range', e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <input type="text" placeholder="Number" value={row.number || ''} onChange={(e) => updateObjectArrayItem('consultancy_offered', i, 'number', e.target.value)} className="w-28 px-3 py-2 border rounded text-sm" />
                <Button onClick={() => removeArrayItem('consultancy_offered', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* CONTRACT RESEARCH SPECIFIC */}
      {slug === 'contract-research' && (
        <>
          {/* Professional Service Description */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Professional Service Function - Contract Research</h3>
            <RichTextEditor value={service.professional_service || ''} onChange={(html) => updateField(slug, 'professional_service', html)} />
          </div>

          {/* Ongoing Research */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Ongoing Contract Research (Repeatable)</h3>
              <Button onClick={() => addArrayItem('ongoing', { title: '', summary: '', link: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Project</Button>
            </div>
            {service.ongoing?.map((project, i) => (
              <div key={i} className="border rounded p-3 mb-3 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm">Project {i + 1}</span>
                  <Button onClick={() => removeArrayItem('ongoing', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <input type="text" placeholder="Project Title" value={project.title || ''} onChange={(e) => updateObjectArrayItem('ongoing', i, 'title', e.target.value)} className="w-full px-3 py-2 border rounded mb-2" />
                <textarea placeholder="Summary" value={project.summary || ''} onChange={(e) => updateObjectArrayItem('ongoing', i, 'summary', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded mb-2" />
                <input type="text" placeholder="Link (optional)" value={project.link || ''} onChange={(e) => updateObjectArrayItem('ongoing', i, 'link', e.target.value)} className="w-full px-3 py-2 border rounded" />
              </div>
            ))}
          </div>

          {/* Completed Research */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Completed Contract Research (Repeatable)</h3>
              <Button onClick={() => addArrayItem('completed', { title: '', summary: '', year: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Project</Button>
            </div>
            {service.completed?.map((project, i) => (
              <div key={i} className="border rounded p-3 mb-3 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm">Project {i + 1}</span>
                  <Button onClick={() => removeArrayItem('completed', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <input type="text" placeholder="Project Title" value={project.title || ''} onChange={(e) => updateObjectArrayItem('completed', i, 'title', e.target.value)} className="w-full px-3 py-2 border rounded mb-2" />
                <textarea placeholder="Summary" value={project.summary || ''} onChange={(e) => updateObjectArrayItem('completed', i, 'summary', e.target.value)} rows={2} className="w-full px-3 py-2 border rounded mb-2" />
                <input type="text" placeholder="Year" value={project.year || ''} onChange={(e) => updateObjectArrayItem('completed', i, 'year', e.target.value)} className="w-full px-3 py-2 border rounded" />
              </div>
            ))}
          </div>
        </>
      )}

      {/* EDUCATION SPECIFIC */}
      {slug === 'education' && (
        <>
          {/* Long Description */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Long Description (Rich Text)</h3>
            <RichTextEditor value={service.description || ''} onChange={(html) => updateField(slug, 'description', html)} />
          </div>
        </>
      )}

      {/* AUDITORIUM SPECIFIC */}
      {slug === 'auditorium' && (
        <>
          {/* Auditorium Gallery */}
          <div className="bg-white p-6 rounded-lg border">
            <h3 className="font-bold text-lg mb-4">Auditorium Gallery (Repeatable Images)</h3>
            <FileUploadInput
              field={{ name: 'gallery', type: 'file', multiple: true, fileType: 'image' }}
              value={fileData.gallery}
              existingUrl={service.gallery?.map(g => g.image) || []}
              onChange={(files) => setFileData({ ...fileData, gallery: files })}
              onDeleteServerFile={(url) => {
                const idx = service.gallery?.findIndex(g => normalizePath(g.image) === normalizePath(url));
                if (idx > -1) {
                  const newGallery = [...(service.gallery || [])];
                  newGallery.splice(idx, 1);
                  updateField(slug, 'gallery', newGallery);
                  return true;
                }
                return false;
              }}
            />
          </div>

          {/* Facilities */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Facilities Available (Repeatable Rows)</h3>
              <Button onClick={() => addArrayItem('facilities', { name: '', description: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Facility</Button>
            </div>
            {service.facilities?.map((facility, i) => (
              <div key={i} className="flex gap-2 mb-2 p-3 border rounded">
                <span className="text-sm font-medium w-8">{i + 1}.</span>
                <input type="text" placeholder="Facility Name" value={facility.name || ''} onChange={(e) => updateObjectArrayItem('facilities', i, 'name', e.target.value)} className="w-1/3 px-3 py-2 border rounded text-sm" />
                <input type="text" placeholder="Details" value={facility.description || ''} onChange={(e) => updateObjectArrayItem('facilities', i, 'description', e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <Button onClick={() => removeArrayItem('facilities', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          {/* Seating & Charges */}
          <div className="bg-white p-6 rounded-lg border space-y-3">
            <h3 className="font-bold text-lg">Seating & Charges</h3>
            <div>
              <label className="text-sm font-medium">Seating Capacity</label>
              <input type="text" value={service.seating_capacity || ''} onChange={(e) => updateField(slug, 'seating_capacity', e.target.value)} className="w-full px-3 py-2 border rounded mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Charges / Booking Terms</label>
              <textarea value={service.charges_text || ''} onChange={(e) => updateField(slug, 'charges_text', e.target.value)} rows={3} className="w-full px-4 py-2 border rounded mt-1" />
            </div>
          </div>
        </>
      )}

      {/* GUEST HOUSE SPECIFIC */}
      {slug === 'guest-house' && (
        <>
          {/* Guest House Gallery */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Guest House Gallery (Repeatable Images)</h3>
              <Button onClick={addGalleryImage} size="sm"><Plus className="w-4 h-4 mr-1" />Add Image</Button>
            </div>
            {service.gallery?.map((img, i) => (
              <div key={i} className="flex gap-2 mb-2 p-3 border rounded">
                <span className="text-sm font-medium w-8">{i + 1}.</span>
                <input
                  type="text"
                  placeholder="Or paste image URL"
                  value={img.image || ''}
                  onChange={(e) => updateGalleryImage(i, 'image', e.target.value)}
                  className="flex-1 px-3 py-2 border rounded text-sm"
                />
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) onGalleryImageUpload(i, file);
                    }}
                    className="hidden"
                    id={`gallery-upload-guesthouse-${i}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById(`gallery-upload-guesthouse-${i}`).click()}
                    disabled={uploadingGallery || saving}
                  >
                    {uploadingGallery ? <Upload className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                <Button onClick={() => removeGalleryImage(i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>

          {/* Contacts */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">Contact Persons (Repeatable)</h3>
              <Button onClick={() => addArrayItem('contacts', { name: '', role: '', phone: '' })} size="sm"><Plus className="w-4 h-4 mr-1" />Add Contact</Button>
            </div>
            {service.contacts?.map((contact, i) => (
              <div key={i} className="border rounded p-3 mb-3 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-sm">Contact {i + 1}</span>
                  <Button onClick={() => removeArrayItem('contacts', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
                </div>
                <input type="text" placeholder="Name" value={contact.name || ''} onChange={(e) => updateObjectArrayItem('contacts', i, 'name', e.target.value)} className="w-full px-3 py-2 border rounded mb-2" />
                <input type="text" placeholder="Role" value={contact.role || ''} onChange={(e) => updateObjectArrayItem('contacts', i, 'role', e.target.value)} className="w-full px-3 py-2 border rounded mb-2" />
                <input type="text" placeholder="Phone" value={contact.phone || ''} onChange={(e) => updateObjectArrayItem('contacts', i, 'phone', e.target.value)} className="w-full px-3 py-2 border rounded" />
              </div>
            ))}
          </div>

          {/* Booking Files */}
          <div className="bg-white p-6 rounded-lg border space-y-3">
            <h3 className="font-bold text-lg">Booking Files</h3>
            <div>
              <label className="text-sm font-medium">Guidelines PDF URL</label>
              <input type="text" value={service.guidelines_pdf || ''} onChange={(e) => updateField(slug, 'guidelines_pdf', e.target.value)} className="w-full px-3 py-2 border rounded mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Booking Form PDF URL</label>
              <input type="text" value={service.booking_form_pdf || ''} onChange={(e) => updateField(slug, 'booking_form_pdf', e.target.value)} className="w-full px-3 py-2 border rounded mt-1" />
            </div>
          </div>

          {/* How to Book Steps */}
          <div className="bg-white p-6 rounded-lg border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">How to Book Steps (Repeatable)</h3>
              <Button onClick={() => addArrayItem('how_to_book_steps', '')} size="sm"><Plus className="w-4 h-4 mr-1" />Add Step</Button>
            </div>
            {service.how_to_book_steps?.map((step, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <span className="text-sm font-medium w-8 pt-2">{i + 1}.</span>
                <input type="text" placeholder={`Step ${i + 1}`} value={step} onChange={(e) => updateArrayItem('how_to_book_steps', i, e.target.value)} className="flex-1 px-3 py-2 border rounded text-sm" />
                <Button onClick={() => removeArrayItem('how_to_book_steps', i)} size="sm" variant="ghost"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Contact Info (All Pages) */}
      <div className="bg-white p-6 rounded-lg border space-y-4">
        <h3 className="font-bold text-lg">Contact Information</h3>
        <div className="grid grid-cols-2 gap-3">
          <input type="text" placeholder="Name" value={service.contact?.name || ''} onChange={(e) => updateField(slug, 'contact', { ...service.contact, name: e.target.value })} className="px-3 py-2 border rounded" />
          <input type="text" placeholder="Role" value={service.contact?.role || ''} onChange={(e) => updateField(slug, 'contact', { ...service.contact, role: e.target.value })} className="px-3 py-2 border rounded" />
          <input type="text" placeholder="Phone" value={service.contact?.phone || ''} onChange={(e) => updateField(slug, 'contact', { ...service.contact, phone: e.target.value })} className="px-3 py-2 border rounded" />
          <input type="text" placeholder="Email" value={service.contact?.email || ''} onChange={(e) => updateField(slug, 'contact', { ...service.contact, email: e.target.value })} className="px-3 py-2 border rounded" />
        </div>
      </div>

      {/* No extra save button here, top one handles it */}
    </div>
  );
}
